import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { parseISO, isAfter, min, max, format, isValid } from 'date-fns';
import invariant from 'invariant';
import * as R from 'ramda';

import { useShortcodeSettings, eventsApiResponseSchema } from '..';
import {
	filterByShortcode,
	filterByShortcodeVenue,
	filterByShortcodeLocation,
	filterOutExpiredEvents,
} from './filters';
import { useCalendarConfig } from './CalendarConfigContext';
import { normalizeEvent, toArray } from './utils';

import type { IEvent } from '..';

type DateRange = { start: string; end: string };
type EventsMap = Map<string, IEvent>;

interface SourceMeta {
	hasMore: boolean;
	offset: number;
	total: number;
}

interface EventBlock extends SourceMeta {
	source: string;
	events: IEvent[];
}

type EventsApiResponse = Record<string, EventBlock> & {
	'events-calendar-plus-version'?: string;
};

type SourceState = Record<string, SourceMeta>;

interface CalendarDataContextType {
	eventsMap: EventsMap;
	getAllEvents: () => IEvent[];
	fetchAllEventsInRange: (range?: DateRange) => Promise<void>;
}

const CalendarDataContext = createContext<CalendarDataContextType | undefined>(undefined);

const fetchEventsFromAPI = async (
	range?: DateRange,
	offsets?: Record<string, number>,
	api?: string
): Promise<EventsApiResponse> => {
	const body: Record<string, unknown> = {};
	if (range) body.dateRange = range;
	if (Object.keys(offsets).length) body.offsets = offsets;
	const apiUrl = api || window?.calendarPlusSettings?.apiUrl;
	const res = await fetch(`${apiUrl}/events`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});

	if (!res.ok) {
		throw new Error('Failed to fetch event data');
	}

	const data = await res.json();

	try {
		await eventsApiResponseSchema.validate(data);
	} catch (err) {
		console.error('API response validation failed:', err);
		throw new Error('Unexpected API structure');
	}

	return data;
};

// --- Month helpers ---
const getMonthKeysInRange = (startStr: string, endStr: string): string[] => {
	const keys: string[] = [];
	const start = new Date(startStr);
	const end = new Date(endStr);

	if (isNaN(start.getTime()) || isNaN(end.getTime())) {
		throw new Error(`Invalid date range: start:"${startStr}", end:"${endStr}"`);
	}

	const date = new Date(start.getFullYear(), start.getMonth(), 1);

	while (date <= end) {
		const year = date.getFullYear();
		const month = `${date.getMonth() + 1}`.padStart(2, '0');
		keys.push(`${year}-${month}`);
		date.setMonth(date.getMonth() + 1);
	}

	return keys;
};

const clipRangeToConfig = (range: DateRange, configRange?: { start?: string; end?: string }): DateRange | null => {
	try {
		if (!range || !range.start || !range.end) return range;

		const startDate = parseISO(range.start);
		const endDate = parseISO(range.end);

		if (!isValid(startDate) || !isValid(endDate)) return range;

		const configStart = configRange.start ? parseISO(configRange.start) : null;
		const configEnd = configRange.end ? parseISO(configRange.end) : null;

		const clippedStart = configStart ? max([startDate, configStart]) : startDate;
		const clippedEnd = configEnd ? min([endDate, configEnd]) : endDate;

		if (isAfter(clippedStart, clippedEnd)) return null;

		return {
			start: format(clippedStart, 'yyyy-MM-dd'),
			end: format(clippedEnd, 'yyyy-MM-dd'),
		};
	} catch (err) {
		console.error('Error in clipRangeToConfig:', err);
		return range;
	}
};

export const CalendarDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { settings: shortcodeSettings } = useShortcodeSettings();
	const { api: apiUrl, dateStart, dateEnd } = useCalendarConfig();

	const [eventsMap, setEventsMap] = useState<EventsMap>(new Map());
	// Tracks fetched months in "YYYY-MM" format.
	const [fetchedMonths, setFetchedMonths] = useState<Set<string>>(new Set());

	const getAllEvents = useCallback((): IEvent[] => {
		const allEvents: IEvent[] = Array.from(eventsMap.values());
		const applyShortcodeFilters = R.pipe(
			R.filter(filterByShortcodeLocation(toArray(shortcodeSettings.countries), 'country')),
			R.filter(filterByShortcodeLocation(toArray(shortcodeSettings.states), 'state')),
			R.filter(filterByShortcodeLocation(toArray(shortcodeSettings.cities), 'city')),
			R.filter(filterByShortcodeVenue(shortcodeSettings.venue)),
			R.filter(filterByShortcode(toArray(shortcodeSettings.cat), 'eventType')),
			R.filter(filterByShortcode(toArray(shortcodeSettings.tag), 'tags')),
			R.filter(filterOutExpiredEvents(shortcodeSettings.expired))
		);

		return applyShortcodeFilters(allEvents);
	}, [eventsMap, shortcodeSettings]);

	const fetchEventsInRange = useCallback(
		async (range?: DateRange, offsets?: Record<string, number>): Promise<SourceState> => {
			try {
				const data = await fetchEventsFromAPI(range, offsets, apiUrl);

				setEventsMap((prevMap) => {
					const newMap = new Map(prevMap);
					for (const key in data) {
						const { source, events } = data[key] as EventBlock;
						if (!source || !Array.isArray(events)) continue;

						for (const event of events) {
							if (!newMap.has(event.UID)) {
								newMap.set(event.UID, normalizeEvent(event));
							}
						}
					}
					return newMap;
				});

				const newSourceState: SourceState = {};
				for (const key in data) {
					const { source, hasMore, offset, total } = data[key] as EventBlock;
					if (!source) continue;
					newSourceState[source] = { hasMore, offset, total };
				}
				return newSourceState;
			} catch (error) {
				console.error('Failed to fetch events:', error);
				return {};
			}
		},
		[setEventsMap, apiUrl]
	);

	const fetchAllEventsInRange = useCallback(
		async (range?: DateRange): Promise<void> => {
			const effectiveRange = range ? clipRangeToConfig(range, { start: dateStart, end: dateEnd }) : undefined;
			if (!effectiveRange) return;

			const rangeMonths = getMonthKeysInRange(effectiveRange.start, effectiveRange.end);
			const monthsToFetch = rangeMonths.filter((m) => !fetchedMonths.has(m));
			if (monthsToFetch.length === 0) {
				return; //All months in range already fetched. Skipping API call.
			}

			let sourceOffsets: Record<string, number> = {};
			let done = false;

			while (!done) {
				try {
					const updatedSourceState = await fetchEventsInRange(effectiveRange, sourceOffsets);
					// Prepare new offsets for sources that still have more
					sourceOffsets = {};
					for (const [source, meta] of Object.entries(updatedSourceState)) {
						if (meta.hasMore) {
							sourceOffsets[source] = meta.offset;
						}
					}
					done = Object.keys(sourceOffsets).length === 0;
				} catch (error) {
					// Stop retrying and exit loop
					console.error('Failed during event pagination:', error);
					done = true;
				}
			}

			for (const month of monthsToFetch) {
				fetchedMonths.add(month);
			}
			setFetchedMonths(fetchedMonths);
		},
		[dateStart, dateEnd, fetchedMonths, fetchEventsInRange, setFetchedMonths]
	);

	const contextProps = useMemo(
		() => ({
			eventsMap,
			getAllEvents,
			fetchAllEventsInRange,
		}),
		[eventsMap, fetchAllEventsInRange, getAllEvents]
	);

	const content =
		apiUrl || window?.calendarPlusSettings?.apiUrl ? (
			children
		) : (
			<div className='events-calendar-plus-loading'>
				<div className='loading'>
					<span>L</span>
					<span>O</span>
					<span>A</span>
					<span>D</span>
					<span>I</span>
					<span>N</span>
					<span>G</span>
				</div>
			</div>
		);

	return <CalendarDataContext.Provider value={contextProps}>{content}</CalendarDataContext.Provider>;
};

export const useCalendarData = (): CalendarDataContextType => {
	const context = useContext(CalendarDataContext);
	invariant(context, 'useCalendarData must be used within a CalendarDataProvider');
	return context;
};
